/**
 * Default label texts and per-industry presets for the org-customizable
 * label aliases (stored in organization.label_aliases).
 *
 * Plain data, server-safe: imported by the client hook (use-alias), the
 * onboarding Setup Wizard AND the AI chat route (terminology context block).
 */

// Default labels (English)
export const DEFAULT_LABELS = {
  // Sales Value Section
  sales_value_title: 'Sales Value',
  sales_value_description: 'Revenue from merchandise and matchday sales.',
  merchandise_revenue: 'Merchandise Revenue',
  matchday_revenue: 'Matchday Revenue',

  // Transfer Value Section
  transfer_value_title: 'Player Sales Value',
  transfer_value_description: 'Track individual player transfers and sales.',

  // Brand Value Section
  brand_value_title: 'Brand Value Indicators',
  brand_value_description: 'Qualitative brand indicators that determine the value multiplier.',

  // Partnership Terminology
  sponsorship_module: 'Sponsorships',
  sponsor_singular: 'Sponsor',
  sponsor_plural: 'Sponsors',
  package_singular: 'Package',
  package_plural: 'Packages',
} as const;

export type LabelKey = keyof typeof DEFAULT_LABELS;

// Example suggestions for different industries
export const LABEL_EXAMPLES: Record<
  LabelKey,
  {
    sports: string; // Sportsklub (default)
    creator: string; // Influencer/Creator
    charity: string; // Nødhjælpsorganisation
    business: string; // Virksomhed / Koncern
    events: string; // Event/Konference
  }
> = {
  sales_value_title: {
    sports: 'Sales Value',
    creator: 'Creator Earnings',
    charity: 'Donation Income',
    business: 'Revenue',
    events: 'Event Revenue',
  },
  sales_value_description: {
    sports: 'Revenue from merchandise and matchday sales.',
    creator: 'Income from social media platforms and brand appearances.',
    charity: 'Donations and fundraising income.',
    business: 'Revenue from product and service sales.',
    events: 'Revenue from tickets and exhibitor sales.',
  },
  merchandise_revenue: {
    sports: 'Merchandise Revenue',
    creator: 'Income from Social Media Platforms',
    charity: 'Donations',
    business: 'Product Revenue',
    events: 'Vendor Revenue',
  },
  matchday_revenue: {
    sports: 'Matchday Revenue',
    creator: 'Appearance Fees & Merch Sales',
    charity: 'Product Sales',
    business: 'Service Revenue',
    events: 'Ticket Revenue',
  },
  transfer_value_title: {
    sports: 'Player Sales Value',
    creator: 'N/A',
    charity: 'N/A',
    business: 'Asset Sales',
    events: 'N/A',
  },
  transfer_value_description: {
    sports: 'Track individual player transfers and sales.',
    creator: 'Not applicable for creators.',
    charity: 'Not applicable for charities.',
    business: 'Track equipment and asset sales.',
    events: 'Not applicable for events.',
  },
  brand_value_title: {
    sports: 'Brand Value Indicators',
    creator: 'Personal Brand Value',
    charity: 'Mission Impact',
    business: 'Brand Strength',
    events: 'Event Brand Impact',
  },
  brand_value_description: {
    sports: 'Qualitative brand indicators that determine the value multiplier.',
    creator: 'Creator reputation metrics that affect earning potential.',
    charity: 'Mission alignment and social impact indicators.',
    business: 'Brand equity factors that influence market value.',
    events: 'Event reputation factors that drive attendance value.',
  },
  sponsorship_module: {
    sports: 'Sponsorships',
    creator: 'Partnerships',
    charity: 'Partnerships',
    business: 'Partnerships',
    events: 'Partnerships',
  },
  sponsor_singular: {
    sports: 'Sponsor',
    creator: 'Brand',
    charity: 'Partner',
    business: 'Client',
    events: 'Partner',
  },
  sponsor_plural: {
    sports: 'Sponsors',
    creator: 'Brands',
    charity: 'Partners',
    business: 'Clients',
    events: 'Partners',
  },
  package_singular: {
    sports: 'Package',
    creator: 'Deal',
    charity: 'Package',
    business: 'Package',
    events: 'Package',
  },
  package_plural: {
    sports: 'Packages',
    creator: 'Deals',
    charity: 'Packages',
    business: 'Packages',
    events: 'Packages',
  },
};
